import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Clock, UtensilsCrossed, Wine, Coffee } from 'lucide-react';

const DiningPage = () => {
  const { t } = useLanguage();

  return (
    <div data-testid="dining-page">
      {/* Hero */}
      <section
        className="relative h-[60vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `url('/images/dining/main_restaurant.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-holiday-purple/60" />
        <div className="relative z-10 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-heading font-bold mb-4">{t('dining.title')}</h1>
          <p className="text-xl text-white/80">{t('dining.subtitle')}</p>
        </div>
      </section>

      {/* Concept Info */}
      <section className="py-20 bg-white">
        <div className="container-custom text-center max-w-3xl mx-auto">
          <p className="text-lg text-gray-600 leading-relaxed mb-4">
            {t('dining.concept')}
          </p>
          <p className="text-holiday-green font-medium">
            {t('dining.premium')}
          </p>
        </div>
      </section>

      {/* Main Restaurant */}
      <section className="section-padding bg-gray-50" data-testid="main-restaurant-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-xl">
              <img
                src="/images/dining/main_restaurant.jpg"
                alt="Main Restaurant"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-holiday-purple/10 rounded-full flex items-center justify-center">
                  <UtensilsCrossed className="w-6 h-6 text-holiday-purple" />
                </div>
                <div>
                  <h2 className="text-3xl font-heading font-bold text-gray-900">
                    {t('dining.mainRestaurant.title')}
                  </h2>
                  <p className="text-holiday-purple">{t('dining.mainRestaurant.subtitle')}</p>
                </div>
              </div>
              <div className="space-y-4 mt-8">
                <div className="flex items-center gap-3 text-gray-600">
                  <Clock className="w-5 h-5 text-holiday-green" />
                  <span>{t('dining.mainRestaurant.breakfast')}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <Clock className="w-5 h-5 text-holiday-green" />
                  <span>{t('dining.mainRestaurant.lateBreakfast')}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <Clock className="w-5 h-5 text-holiday-green" />
                  <span>{t('dining.mainRestaurant.lunch')}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <Clock className="w-5 h-5 text-holiday-green" />
                  <span>{t('dining.mainRestaurant.dinner')}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <Clock className="w-5 h-5 text-holiday-green" />
                  <span>{t('dining.mainRestaurant.nightService')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Snacks Section */}
      <section className="section-padding bg-white" data-testid="snacks-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-holiday-green/10 rounded-full flex items-center justify-center">
                  <Coffee className="w-6 h-6 text-holiday-green" />
                </div>
                <div>
                  <h2 className="text-3xl font-heading font-bold text-gray-900">
                    {t('dining.snacks.title')}
                  </h2>
                  <p className="text-holiday-green">{t('dining.snacks.subtitle')}</p>
                </div>
              </div>
              <ul className="space-y-4 mt-8">
                {t('dining.snacks.items').map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-600">
                    <span className="w-2 h-2 bg-holiday-green rounded-full"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="order-1 lg:order-2 aspect-[4/3] rounded-lg overflow-hidden shadow-xl">
              <img
                src="/images/dining/snack.jpg"
                alt="Snacks"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* A La Carte Section */}
      <section className="section-padding bg-gray-50" data-testid="alacarte-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-heading font-bold text-gray-900 mb-4">
              {t('dining.alaCarte.title')}
            </h2>
            <p className="text-holiday-purple">{t('dining.alaCarte.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-heading font-bold text-gray-900 mb-4">İtalyan Restoran</h3>
              <p className="text-gray-600 mb-4">{t('dining.alaCarte.italian')}</p>
              <img
                src="/images/dining/alacarte.jpg"
                alt="Italian Restaurant"
                className="w-full aspect-video object-cover rounded-lg"
              />
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-heading font-bold text-gray-900 mb-4">Uluslararası Restoran</h3>
              <p className="text-gray-600 mb-4">{t('dining.alaCarte.international')}</p>
              <img
                src="/images/dining/roof_bar.jpg"
                alt="International Restaurant"
                className="w-full aspect-video object-cover rounded-lg"
              />
            </div>
          </div>
          <p className="text-center text-gray-500 mt-8 text-sm">
            {t('dining.alaCarte.note')}
          </p>
        </div>
      </section>

      {/* Bars Section */}
      <section className="section-padding bg-holiday-purple text-white" data-testid="bars-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Wine className="w-12 h-12 mx-auto mb-4" />
            <h2 className="text-4xl font-heading font-bold mb-4">
              {t('dining.bars.title')}
            </h2>
            <p className="text-white/80">{t('dining.bars.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { name: 'Lobby Bar', hours: t('dining.bars.lobby'), icon: '🍸' },
              { name: 'Roof Bar', hours: t('dining.bars.roof'), icon: '🌅' },
              { name: 'Pool Bar', hours: t('dining.bars.pool'), icon: '🏊' },
              { name: 'Snack Bar', hours: t('dining.bars.snack'), icon: '🍹' },
              { name: 'Sahil Bar', hours: t('dining.bars.beach'), icon: '🏖️' },
            ].map((bar, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm p-6 rounded-lg">
                <span className="text-3xl mb-4 block">{bar.icon}</span>
                <h3 className="text-xl font-bold mb-2">{bar.name}</h3>
                <p className="text-white/70 text-sm">{bar.hours}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DiningPage;
